/**
 * SaveAsTemplateModal — E3 · "Guardar como plantilla".
 * Elegís qué clips quedan como HUECOS reemplazables, qué textos como
 * VARIABLES, y qué MP4 ya exportado se usa como vista previa.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { X, Loader2, Save, Film, Image as ImageIcon, AlertTriangle, Replace } from 'lucide-react';
import { toast } from 'sonner';
import useTimelineStore from '../../store/timelineStore';
import { galleryService } from '../../services/galleryService';
import { templateService, grabFrameFromVideo } from '../../services/templateService';

const fmtDur = (s) => `${(Number(s) || 0).toFixed(1)} s`;

const SaveAsTemplateModal = ({ projectId, serverVersion, onClose, onSaved }) => {
  const videoClips = useTimelineStore((s) => s.videoClips);
  const imageClips = useTimelineStore((s) => s.imageClips);
  const overlayV1Clips = useTimelineStore((s) => s.overlayV1Clips);
  const overlayV2Clips = useTimelineStore((s) => s.overlayV2Clips);
  const audioClips = useTimelineStore((s) => s.audioClips);
  const elements = useTimelineStore((s) => s.elements);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [slotIds, setSlotIds] = useState({});       // clip_id → nombre del hueco
  const [varIds, setVarIds] = useState({});         // element_id → nombre
  const [renders, setRenders] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewVersion, setPreviewVersion] = useState(null);
  const [previewAt, setPreviewAt] = useState(0.3);
  const [previewRange, setPreviewRange] = useState({ start: 0, end: 5 });
  const [thumb, setThumb] = useState(null);
  const [thumbError, setThumbError] = useState(false);
  const [pickingGallery, setPickingGallery] = useState(false);
  const [videos, setVideos] = useState(null);
  const [saving, setSaving] = useState(false);
  // Si esta pieza nació de una plantilla, se puede actualizar la original
  // (versión nueva) en vez de crear otra.
  const templateRef = useTimelineStore((s) => s.templateRef);
  const [sourceTemplate, setSourceTemplate] = useState(null);   // undefined = resolviendo

  useEffect(() => {
    if (!templateRef?.template_id) { setSourceTemplate(null); return; }
    let cancelled = false;
    templateService.get(templateRef.template_id)
      .then((t) => { if (!cancelled) setSourceTemplate(t); })
      .catch(() => { if (!cancelled) setSourceTemplate(null); });
    return () => { cancelled = true; };
  }, [templateRef?.template_id]);

  const candidates = useMemo(() => ([
    ...videoClips.map((c) => ({ ...c, trackLabel: 'Video base', kind: 'video' })),
    ...imageClips.map((c) => ({ ...c, trackLabel: 'Imágenes', kind: 'image' })),
    ...overlayV1Clips.map((c) => ({ ...c, trackLabel: 'Overlay 1' })),
    ...overlayV2Clips.map((c) => ({ ...c, trackLabel: 'Overlay 2' })),
    ...audioClips.map((c) => ({ ...c, trackLabel: 'Audio', kind: 'audio' })),
  ]), [videoClips, imageClips, overlayV1Clips, overlayV2Clips, audioClips]);

  const textElements = useMemo(
    () => elements.filter((el) => el.type === 'text'), [elements]);

  useEffect(() => {
    if (!projectId) return;
    templateService.listRenders(projectId)
      .then((list) => {
        setRenders(list);
        if (list[0]) {
          setPreviewUrl(list[0].output_url);
          setPreviewVersion(list[0].timeline_version ?? null);
        }
      })
      .catch(() => setRenders([]));
  }, [projectId]);

  // Miniatura: la genera el navegador desde el MP4 elegido.
  useEffect(() => {
    if (!previewUrl) { setThumb(null); setThumbError(false); return undefined; }
    let cancelled = false;
    setThumbError(false);
    grabFrameFromVideo(previewUrl, previewAt).then(({ dataUrl }) => {
      if (cancelled) return;
      setThumb(dataUrl);
      setThumbError(!dataUrl);
    });
    return () => { cancelled = true; };
  }, [previewUrl, previewAt]);

  const openGallery = async () => {
    setPickingGallery(true);
    if (videos === null) {
      try {
        const data = await galleryService.listAssets({ assetType: 'video', limit: 60 });
        setVideos(data.assets || []);
      } catch (_e) { setVideos([]); }
    }
  };

  const toggleSlot = (clip) => setSlotIds((prev) => {
    const next = { ...prev };
    if (next[clip.id] !== undefined) delete next[clip.id];
    else next[clip.id] = clip.label || 'Hueco';
    return next;
  });

  const toggleVar = (el) => setVarIds((prev) => {
    const next = { ...prev };
    if (next[el.element_id] !== undefined) delete next[el.element_id];
    else next[el.element_id] = el.label || 'Texto';
    return next;
  });

  const buildPayload = useCallback(() => ({
    meta: { name, description, category },
    slots: Object.entries(slotIds).map(([clip_id, slotName]) => ({
      clip_id, name: slotName,
    })),
    textVariables: Object.entries(varIds).map(([element_id, varName]) => ({
      element_id, name: varName,
    })),
    preview: {
      source_url: previewUrl || null,
      thumbnail_data_url: thumb,
      preview_start: previewRange.start,
      preview_end: previewRange.end,
      timeline_version: previewVersion,
    },
  }), [name, description, category, slotIds, varIds, previewUrl, thumb,
    previewRange, previewVersion]);

  const saveNew = useCallback(async () => {
    if (!name.trim()) return toast.error('Ponele un nombre a la plantilla');
    setSaving(true);
    try {
      const template = await templateService.saveAsTemplate(projectId, buildPayload());
      (template.problems || []).forEach((p) => toast.warning(p, { duration: 8000 }));
      toast.success('Plantilla guardada');
      onSaved?.(template);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message
        || err.response?.data?.detail
        || 'No se pudo guardar la plantilla');
    } finally {
      setSaving(false);
    }
  }, [name, buildPayload, projectId, onSaved, onClose]);

  const updateOriginal = useCallback(async () => {
    if (!sourceTemplate) return;
    setSaving(true);
    try {
      const template = await templateService.patch(sourceTemplate.id, {
        project_id: projectId,
        slots: buildPayload().slots,
        text_variables: buildPayload().textVariables,
      });
      const payload = buildPayload();
      if (payload.preview.source_url || payload.preview.thumbnail_data_url) {
        await templateService.setPreview(sourceTemplate.id, {
          source_url: payload.preview.source_url || undefined,
          thumbnail_data_url: payload.preview.thumbnail_data_url || undefined,
          preview_start: payload.preview.preview_start,
          preview_end: payload.preview.preview_end,
          timeline_version: payload.preview.timeline_version,
        });
      }
      toast.success(`"${sourceTemplate.name}" actualizada a la versión ${template.version}`);
      onSaved?.(template);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail?.message
        || err.response?.data?.detail
        || 'No se pudo actualizar la plantilla');
    } finally {
      setSaving(false);
    }
  }, [sourceTemplate, projectId, buildPayload, onSaved, onClose]);

  const stale = previewVersion != null && serverVersion != null
    && Number(previewVersion) !== Number(serverVersion);

  return (
    <div className="tl-gallery-overlay" onClick={onClose} data-testid="tl-save-template-overlay">
      <div className="tl-template-modal" onClick={(e) => e.stopPropagation()} data-testid="tl-save-template-modal">
        <div className="tl-gallery-header">
          <span className="tl-gallery-title">Guardar como plantilla</span>
          <button className="tl-icon-btn" onClick={onClose} data-testid="tl-save-template-close">
            <X size={14} />
          </button>
        </div>

        <div className="tl-template-body">
          <div className="tl-template-fields">
            <input
              type="text" placeholder="Nombre de la plantilla"
              value={name} onChange={(e) => setName(e.target.value)}
              data-testid="tl-template-name"
            />
            <input
              type="text" placeholder="Categoría (ej. Reels, Anuncios)"
              value={category} onChange={(e) => setCategory(e.target.value)}
              data-testid="tl-template-category"
            />
            <textarea
              placeholder="¿Para qué sirve esta plantilla?" rows={2}
              value={description} onChange={(e) => setDescription(e.target.value)}
              data-testid="tl-template-description"
            />
          </div>

          <div className="tl-template-section">
            <h4><Replace size={13} /> Partes reemplazables (huecos)</h4>
            <p>Lo que marques queda vacío al usar la plantilla; el resto es fijo.</p>
            <div className="tl-template-list" data-testid="tl-template-slot-list">
              {candidates.length === 0 && <em>El timeline no tiene clips.</em>}
              {candidates.map((clip) => (
                <label key={clip.id} className={slotIds[clip.id] !== undefined ? 'on' : ''}>
                  <input
                    type="checkbox"
                    checked={slotIds[clip.id] !== undefined}
                    onChange={() => toggleSlot(clip)}
                    data-testid={`tl-template-slot-${clip.id}`}
                  />
                  <span className="tl-template-list-main">
                    <strong>{clip.label || clip.kind}</strong>
                    <small>{clip.trackLabel} · {fmtDur(clip.duration)}</small>
                  </span>
                  {slotIds[clip.id] !== undefined && (
                    <input
                      type="text" className="tl-template-slot-name"
                      value={slotIds[clip.id]}
                      onChange={(e) => setSlotIds((p) => ({ ...p, [clip.id]: e.target.value }))}
                      placeholder="Nombre del hueco"
                      data-testid={`tl-template-slot-name-${clip.id}`}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="tl-template-section">
            <h4>Textos editables (variables)</h4>
            <div className="tl-template-list" data-testid="tl-template-var-list">
              {textElements.length === 0 && <em>No hay textos en el timeline.</em>}
              {textElements.map((el) => (
                <label key={el.element_id} className={varIds[el.element_id] !== undefined ? 'on' : ''}>
                  <input
                    type="checkbox"
                    checked={varIds[el.element_id] !== undefined}
                    onChange={() => toggleVar(el)}
                    data-testid={`tl-template-var-${el.element_id}`}
                  />
                  <span className="tl-template-list-main">
                    <strong>{(el.style?.content || 'Texto').slice(0, 40)}</strong>
                    <small>texto · {fmtDur(el.duration)}</small>
                  </span>
                  {varIds[el.element_id] !== undefined && (
                    <input
                      type="text" className="tl-template-slot-name"
                      value={varIds[el.element_id]}
                      onChange={(e) => setVarIds((p) => ({ ...p, [el.element_id]: e.target.value }))}
                      data-testid={`tl-template-var-name-${el.element_id}`}
                    />
                  )}
                </label>
              ))}
            </div>
          </div>

          <div className="tl-template-section">
            <h4><Film size={13} /> Video de muestra</h4>
            <p>Se reusa un MP4 que ya exportaste: no se renderiza nada nuevo.</p>
            {renders === null ? (
              <div className="tl-template-loading"><Loader2 size={14} className="spin" /> Buscando exportaciones…</div>
            ) : (
              <>
                <select
                  value={previewUrl}
                  onChange={(e) => {
                    setPreviewUrl(e.target.value);
                    const found = (renders || []).find((r) => r.output_url === e.target.value);
                    setPreviewVersion(found?.timeline_version ?? null);
                  }}
                  data-testid="tl-template-preview-select"
                >
                  <option value="">Sin video de muestra</option>
                  {(renders || []).map((r) => (
                    <option key={r.execution_id} value={r.output_url}>
                      {new Date(r.created_at).toLocaleString('es')} · {r.resolution} · {fmtDur(r.duration_seconds)}
                    </option>
                  ))}
                </select>
                <button className="tl-template-link" onClick={openGallery} data-testid="tl-template-pick-gallery">
                  Elegir de la galería
                </button>
                {renders.length === 0 && (
                  <em className="tl-template-note" data-testid="tl-template-no-renders">
                    Todavía no exportaste este timeline: la plantilla se guarda sin video y
                    podés agregarlo después desde el detalle.
                  </em>
                )}
                {stale && (
                  <div className="tl-template-warn" data-testid="tl-template-stale">
                    <AlertTriangle size={12} />
                    Este video es de una versión anterior del timeline. Podés usarlo igual
                    o exportar de nuevo.
                  </div>
                )}
              </>
            )}

            {previewUrl && (
              <div className="tl-template-preview">
                {thumb
                  ? <img src={thumb} alt="miniatura" data-testid="tl-template-thumb" />
                  : (
                    <div className="tl-template-thumb-empty" data-testid="tl-template-thumb-error">
                      <ImageIcon size={18} />
                      {thumbError
                        ? 'No se pudo leer el video para la miniatura (CORS de R2)'
                        : 'Generando miniatura…'}
                    </div>
                  )}
                <div className="tl-template-preview-controls">
                  <label>
                    Cuadro de la miniatura
                    <input
                      type="range" min="0" max="1" step="0.05" value={previewAt}
                      onChange={(e) => setPreviewAt(Number(e.target.value))}
                      data-testid="tl-template-thumb-at"
                    />
                    <small>{Math.round(previewAt * 100)}% del video</small>
                  </label>
                  <label>
                    Fragmento de muestra (s)
                    <span className="tl-template-range">
                      <input
                        type="number" min="0" step="0.5" value={previewRange.start}
                        onChange={(e) => setPreviewRange((p) => ({ ...p, start: Number(e.target.value) }))}
                        data-testid="tl-template-preview-start"
                      />
                      <input
                        type="number" min="0.5" step="0.5" value={previewRange.end}
                        onChange={(e) => setPreviewRange((p) => ({ ...p, end: Number(e.target.value) }))}
                        data-testid="tl-template-preview-end"
                      />
                    </span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="tl-template-actions">
          {sourceTemplate && (
            <button
              className="tl-export-btn tl-tpl-update" onClick={updateOriginal} disabled={saving}
              data-testid="tl-template-update-original"
            >
              {saving ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
              Actualizar “{sourceTemplate.name}”
            </button>
          )}
          <button
            className="tl-export-btn" onClick={saveNew} disabled={saving}
            data-testid="tl-template-save"
          >
            {saving ? <Loader2 size={13} className="spin" /> : <Save size={13} />}
            {sourceTemplate ? 'Guardar como plantilla nueva' : 'Guardar plantilla'}
          </button>
          <span className="tl-template-summary" data-testid="tl-template-summary">
            {Object.keys(slotIds).length} hueco(s) · {Object.keys(varIds).length} texto(s) editable(s)
          </span>
        </div>

        {pickingGallery && (
          <div className="tl-template-gallery" data-testid="tl-template-gallery">
            <div className="tl-template-gallery-head">
              <strong>Elegí un MP4 de tu galería</strong>
              <button className="tl-icon-btn" onClick={() => setPickingGallery(false)}>
                <X size={13} />
              </button>
            </div>
            <div className="tl-gallery-grid">
              {videos === null && <div className="tl-gallery-empty"><Loader2 size={16} className="spin" /></div>}
              {videos?.length === 0 && <div className="tl-gallery-empty">No hay videos.</div>}
              {videos?.map((v) => (
                <button
                  key={v.id} className="tl-gallery-card"
                  onClick={() => {
                    setPreviewUrl(v.url);
                    setPreviewVersion(null);
                    setPickingGallery(false);
                  }}
                  data-testid={`tl-template-gallery-${v.id}`}
                >
                  {v.thumbnail_url
                    ? <img src={v.thumbnail_url} alt={v.filename} loading="lazy" />
                    : <div className="tl-gallery-audio-icon"><Film size={24} /></div>}
                  <span className="tl-gallery-filename">{v.filename}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SaveAsTemplateModal;
